import { EntityCollection } from '@fairandsmart/types';
import { EMPTY, Observable } from 'rxjs';
import { finalize, mergeMap, tap } from 'rxjs/operators';
import {
  FlexIndexableEntity,
  FlexIndexationHandler,
  FlexIndexationProgress,
  FlexIndexationStatus,
  FlexIndexDocument,
  FlexIndexEvent,
  FlexIndexEventType,
  FlexSearchService,
} from '../flex-search.service';

export abstract class FlexEntityIndexer<T extends FlexIndexableEntity, F> implements FlexIndexationHandler<T> {
  private indexing: boolean;

  private indexationProgress: FlexIndexationProgress;

  private readonly indexingStatusRef: string;

  protected constructor(
    protected readonly id: string,
    protected flexSearch: FlexSearchService,
    protected readonly batchNumber: number = 25,
  ) {
    this.indexingStatusRef = `${this.id}_indexing-status`;
    this.indexing = false;
    this.flexSearch.registerHandler(this);
  }

  public abstract handle(entity: T): Observable<FlexIndexDocument>;

  public abstract isHandling(entity: FlexIndexableEntity): boolean;

  protected abstract search(filter: F): Observable<EntityCollection<T>>;

  protected abstract getFilter(since: number, offset: number): F;

  public startBackgroundIndexation(): void {
    this.flexSearch.ready.pipe(
      tap((ready: boolean) => {
        if (ready) {
          if (!this.indexing) {
            const status = this.flexSearch.getDocument<FlexIndexationStatus>(this.indexingStatusRef);
            this.indexationProgress = { size: 0, done: 0, error: 0 };
            this.flexSearch.setProgress(this.id, this.indexationProgress);
            this.backgroundIndexation(status == null || status.last == null ? 0 : status.last + 1, 0);
          } else {
            console.error(`[indexation:${this.id}] Already indexing`);
          }
        } else if (this.indexing) {
          // index service not ready stop background indexation if running
        }
      }),
    ).subscribe();
  }

  private backgroundIndexation(since: number, offset: number): void {
    console.info(`[indexation:${this.id}] background indexation (since: ${since}, offset: ${offset})`);
    this.search(this.getFilter(since, offset)).pipe(
      mergeMap((response: EntityCollection<T>) => {
        if (response.values.length === 0) {
          console.info(`[indexation:${this.id}] background indexation done !`);
          return EMPTY;
        }
        this.indexationProgress.size = response.size;
        this.flexSearch.setProgress(this.id, this.indexationProgress);
        return this.index(response.values, { highPriority: false, addToRegistry: false }).pipe(
          tap((event: FlexIndexEvent) => {
            if (event.type === FlexIndexEventType.INDEXATION_ERROR) {
              this.indexationProgress.error += 1;
            } else {
              this.indexationProgress.done += 1;
            }
            this.flexSearch.setProgress(this.id, this.indexationProgress);
            if (this.indexationProgress.size === this.indexationProgress.done) {
              const currentSize = this.indexationProgress.size;
              window.setTimeout(() => {
                if (this.indexationProgress.size === currentSize && this.indexationProgress.size === this.indexationProgress.done) {
                  this.indexationProgress.size = 0;
                  this.indexationProgress.done = 0;
                  this.indexationProgress.error = 0;
                  this.flexSearch.setProgress(this.id, this.indexationProgress);
                }
              }, 1000);
            }
          }),
          finalize(() => {
            const status: FlexIndexationStatus = {
              id: this.indexingStatusRef,
              last: response.values[response.values.length - 1].modificationDate,
            };
            this.flexSearch.addStatusDocument(status);
            if (response.size <= response.offset + response.values.length) {
              console.info(`[indexation:${this.id}] background indexation done !`);
              return;
            }
            this.backgroundIndexation(since, offset + this.batchNumber);
          }),
        );
      }),
    ).subscribe();
  }

  protected index(entities: FlexIndexableEntity[], options: { highPriority: boolean, addToRegistry: boolean, reindex?: boolean })
    : Observable<any> {
    return this.flexSearch.queueEntities(entities, options.highPriority, true);
  }
}
